import { useEffect, useState } from "react";
import { approvedView, pendingChangeText, type SoaApprovalEntry } from "../lib/soaApproval";
import { CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { theme, emptyFrame, btnSecondary, type as text } from "../theme";
import { api } from "../api/client";
import { useExperience } from "../context/ExperienceContext";
import { useControlTagData, useTagFilter, matchesTagFilter, isTagFilterActive, describeTagFilter } from "./ControlTags";
import { frameworkAccent } from "./controlStatus";
import { displayControlRef, displayControlTitle } from "../lib/controlRef";
import { DetailSection, EditButton } from "./RecordView";
import { ControlGroup, ControlGroupStack, ControlListFrame } from "./standards/ControlGroup";
import { ControlRow } from "./standards/ControlRow";
import { ControlDetail, evidenceCountFor, type EvidenceDoc } from "./standards/ControlDetail";
import type { RecordCheckResult } from "./RecordChecks";
import { ControlFilterBar, matchesControlSearch, isGroupOpen, toggleGroup, type GroupExpansion } from "./standards/ControlFilterBar";
import { SummaryBar } from "./standards/StatusSummary";
import { GuidedIntro } from "./standards/StandardHeader";
import { useStandardPermissions } from "./standards/useStandardPermissions";
import { RequirementSatisfaction } from "./RequirementSatisfaction";

// The Statement of Applicability of a standard with Annex / supplementary controls (ISO 27001,
// ISO 42001, ISO 9001, ISO 45001, ISO 14001, NCSC CAF). Same summary, filter bar, groups, rows
// and detail as the assessment pages (components/standards/*); what only the SoA has is the
// applicability decision, shown on the row and changed from the detail's Applicability section.

export interface SoaRow {
  control: {
    id: string;
    frameworkId: string;
    kind: "CLAUSE" | "CONTROL";
    domain: string | null;
    name: string;
    description: string;
    translatedName: string | null;
    translatedDescription: string | null;
    translatedRequiredEvidence: string[] | null;
  };
  entry: SoaApprovalEntry & { applicable: boolean | null; justification: string | null; approvalStatus: string; aiNote?: string | null };
  /** Record checks on this control, from GET /controls/soa (absent when none bear on it). */
  recordChecks?: RecordCheckResult[];
}

interface Props {
  frameworkId: string;
  consultantMode?: boolean;
  /** Singular noun for counts ("control", "outcome"). */
  noun?: string;
}

function splitDomain(domain: string): { title: string; hint?: string } {
  const i = domain.lastIndexOf(" · ");
  return i > 0 ? { title: domain.slice(i + 3), hint: domain.slice(0, i) } : { title: domain };
}

export function SoaFrameworkPanel({ frameworkId, consultantMode, noun = "control" }: Props) {
  const { novice } = useExperience();
  const { t, i18n } = useTranslation();
  const hasTranslations = i18n.language !== "en";
  const accent = frameworkAccent(frameworkId);
  const { canAssess, canAttach } = useStandardPermissions();

  const [allRows, setAllRows] = useState<SoaRow[] | null>(null);
  const [docs, setDocs] = useState<EvidenceDoc[]>([]);
  const [linkedRisksMap, setLinkedRisksMap] = useState<Map<string, number>>(new Map());
  // Each control's own standing at the sites that have one (backend lib/siteStandards.ts): the row
  // shows the weakest beside the organisation's status, which it never changes.
  const [siteStanding, setSiteStanding] = useState<Record<string, Array<{ siteId: string; siteName: string; status: string }>>>({});
  // Department / office / jurisdiction tags: one shared store and row for every
  // assessment page (components/ControlTags.tsx). ?site= etc. seed the filter.
  const tagData = useControlTagData();
  const [tagFilter, setTagFilter] = useTagFilter();
  const [search, setSearch] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [expansion, setExpansion] = useState<GroupExpansion>("all");

  async function loadAll() {
    const [soaData, docsData, risksData] = await Promise.all([
      api.get<{ soa: SoaRow[] }>("/controls/soa"),
      api.get<{ documents: EvidenceDoc[] }>("/documents"),
      api.get<{ risks: Array<{ linkedControlId?: string | null }> }>("/risks"),
    ]);
    setAllRows(soaData.soa.filter((r) => r.control.frameworkId === frameworkId && r.control.kind === "CONTROL"));
    setDocs(docsData.documents);
    const rMap = new Map<string, number>();
    for (const r of risksData.risks) {
      if (r.linkedControlId) rMap.set(r.linkedControlId, (rMap.get(r.linkedControlId) ?? 0) + 1);
    }
    setLinkedRisksMap(rMap);
    api.get<{ controls: typeof siteStanding }>("/site-compliance/controls", { frameworkId })
      .then((d) => setSiteStanding(d.controls))
      .catch(() => setSiteStanding({}));
  }

  // The weakest site result on a control (a gap before work in progress before met); not applicable is left out.
  function weakestSite(controlId: string) {
    const rank: Record<string, number> = { GAP: 0, IN_PROGRESS: 1, COMPLIANT: 2 };
    const rows = (siteStanding[controlId] ?? []).filter((r) => r.status in rank);
    if (rows.length === 0) return null;
    const worst = [...rows].sort((a, b) => rank[a.status] - rank[b.status])[0];
    return { worst, all: rows };
  }

  useEffect(() => { loadAll(); }, [frameworkId]);

  async function updateEntry(controlId: string, patch: Partial<SoaRow["entry"]>) {
    setAllRows((prev) =>
      prev && prev.map((r) => r.control.id === controlId ? { ...r, entry: { ...r.entry, ...patch } } : r)
    );
    const { entry: saved } = await api.put<{ entry: SoaRow["entry"] }>(`/controls/soa/${controlId}`, patch);
    // Reconcile approvalStatus from the server — the backend decides whether COMPLIANT
    // goes to DRAFT (consultant linked) or APPROVED (no consultant).
    setAllRows((prev) =>
      prev && prev.map((r) => r.control.id === controlId ? { ...r, entry: { ...r.entry, ...patch, approvalStatus: saved.approvalStatus } } : r)
    );
  }

  async function selectAllApplicable(visible: SoaRow[]) {
    const toUpdate = visible.filter((r) => r.entry.applicable !== true);
    if (!toUpdate.length) return;
    setBulkLoading(true);
    setAllRows((prev) =>
      prev && prev.map((r) =>
        toUpdate.some((u) => u.control.id === r.control.id) ? { ...r, entry: { ...r.entry, applicable: true } } : r
      )
    );
    await Promise.all(toUpdate.map((r) => api.put(`/controls/soa/${r.control.id}`, { applicable: true })));
    setBulkLoading(false);
  }

  async function resetEntry(controlId: string) {
    await api.del(`/controls/soa/${controlId}`);
    await loadAll();
  }

  async function reloadDocs() {
    const { documents } = await api.get<{ documents: EvidenceDoc[] }>("/documents");
    setDocs(documents);
  }

  if (!allRows) return <div style={{ ...text.hint, padding: "16px 0" }}>Loading…</div>;

  const nameOf = (r: SoaRow) => (hasTranslations && r.control.translatedName) ? r.control.translatedName : r.control.name;
  const refOf = (r: SoaRow) => displayControlRef(r.control.id);
  const titleOf = (r: SoaRow) => displayControlTitle(r.control.id, nameOf(r));

  const domains = Array.from(new Set(allRows.filter((r) => r.control.domain).map((r) => r.control.domain as string)));
  const visible = allRows.filter((r) =>
    matchesTagFilter(tagData, r.control.id, tagFilter) && matchesControlSearch(search, refOf(r), titleOf(r), r.control.id, r.control.name));
  const grouped = domains
    .map((d) => ({ domain: d, ...splitDomain(d), rows: visible.filter((r) => r.control.domain === d) }))
    .filter((g) => g.rows.length > 0);
  const ungrouped = visible.filter((r) => !r.control.domain);
  const searching = search.trim().length > 0;

  const allApplicable = visible.every((r) => r.entry.applicable === true);
  const soaTotal = allRows.length;
  const soaApplicable = allRows.filter((r) => r.entry.applicable === true).length;
  const soaNotApplicable = allRows.filter((r) => r.entry.applicable === false).length;
  const soaUnset = soaTotal - soaApplicable - soaNotApplicable;
  const nounPlural = `${noun}s`;
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  function applicabilityPill(applicable: boolean | null) {
    const label = applicable === true ? (novice ? "Applies to us" : "Applicable")
      : applicable === false ? (novice ? "Excluded" : "Not applicable")
      : (novice ? "Not decided" : "Not reviewed");
    const on = applicable === true;
    return (
      <span style={{ ...text.meta, fontWeight: 700, padding: "2px 8px", borderRadius: 3, whiteSpace: "nowrap", color: on ? theme.color.accentText : theme.color.subtle, background: on ? theme.color.accentBg : theme.color.panelAlt, border: `1px solid ${on ? theme.color.accent : theme.color.borderStrong}` }}>
        {label}
      </span>
    );
  }

  function renderRow(row: SoaRow) {
    const isOpen = open === row.control.id;
    const risks = linkedRisksMap.get(row.control.id) ?? 0;
    const pending = row.entry.approvalStatus === "DRAFT";
    const description = (hasTranslations && row.control.translatedDescription) ? row.control.translatedDescription : row.control.description;
    return (
      <ControlRow
        key={row.control.id}
        reference={refOf(row)}
        title={titleOf(row)}
        status={row.entry.status}
        novice={novice}
        pending={pending}
        evidenceCount={evidenceCountFor(docs, row.control.frameworkId, row.control.id)}
        hasNotes={!!row.entry.justification}
        dimmed={row.entry.applicable === false}
        // Deciding applicability down the whole list is the job of this page, so people who may
        // change it get the quick toggle on the row; everyone else sees the decision as a pill.
        quickAction={canAssess ? (
          <ApplicabilityToggle
            value={row.entry.applicable}
            novice={novice}
            label={titleOf(row)}
            onChange={(value) => { void updateEntry(row.control.id, { applicable: value }); }}
          />
        ) : undefined}
        extra={<>
          {!canAssess && applicabilityPill(row.entry.applicable)}
          {risks > 0 && (
            <span style={{ ...text.meta, fontWeight: 600, padding: "2px 7px", borderRadius: 3, background: theme.color.warningBg, color: theme.color.warningText, border: `1px solid ${theme.color.warning}`, whiteSpace: "nowrap" }}>
              {risks} risk{risks !== 1 ? "s" : ""} linked
            </span>
          )}
          {(() => {
            const w = weakestSite(row.control.id);
            if (!w) return null;
            const word = w.worst.status === "GAP" ? "Gap" : w.worst.status === "IN_PROGRESS" ? "In progress" : "Met";
            const tone = w.worst.status === "GAP" ? "dangerText" : w.worst.status === "IN_PROGRESS" ? "warningText" : "successText";
            return (
              <span
                title={`At each site with its own result: ${w.all.map((r) => `${r.siteName} — ${r.status === "GAP" ? "gap" : r.status === "IN_PROGRESS" ? "in progress" : "met"}`).join("; ")}`}
                style={{ ...text.meta, fontWeight: 600, padding: "2px 7px", borderRadius: 2, border: `1px solid var(--c-${tone})`, color: `var(--c-${tone})`, whiteSpace: "nowrap" }}
              >
                {word} at {w.worst.siteName}{w.all.length > 1 ? ` +${w.all.length - 1}` : ""}
              </span>
            );
          })()}
        </>}
        open={isOpen}
        onToggle={() => setOpen(isOpen ? null : row.control.id)}
      >
        <ControlDetail
          controlId={row.control.id}
          requirement={
            <>
              {description}
              {hasTranslations && row.control.translatedDescription && (
                <OriginalText label={t("translation.showOriginal")} hideLabel={t("translation.hideOriginal")} text={row.control.description} />
              )}
            </>
          }
          sections={
            <>
              <ApplicabilitySection
                applicable={row.entry.applicable}
                novice={novice}
                canEdit={canAssess}
                onChange={(value) => updateEntry(row.control.id, { applicable: value })}
              />
              {/* Re-keyed on status so a change on the row re-reads what evidences it. */}
              <RequirementSatisfaction key={`${row.control.id}:${row.entry.status}:${row.entry.applicable}`} controlId={row.control.id} />
            </>
          }
          status={row.entry.status}
          novice={novice}
          pending={pending}
          // The draft may be a status, an exclusion or a note: only an exclusion is told apart, the
          // rest is "a change" rather than a claim about the status the client may not have touched.
          pendingText={pendingChangeText(row.entry)}
          rejectionReason={row.entry.rejectionReason}
          aiNote={row.entry.aiNote}
          recordChecks={row.recordChecks}
          canEdit={canAssess}
          evidence={{ docs, frameworkId: row.control.frameworkId, controlRef: row.control.id, reload: reloadDocs }}
          canAttach={canAttach}
          notes={{
            threadId: row.control.id,
            label: novice ? "Notes" : "Justification notes",
            consultantMode,
            legacyNote: row.entry.justification,
            onPost: (body) => { updateEntry(row.control.id, { justification: body }).catch(() => { /* the note itself is saved; the mirror is best-effort */ }); },
          }}
          actions={(row.entry.applicable !== null || row.entry.justification || row.entry.status !== "NOT_STARTED")
            ? <ResetButton onConfirm={() => resetEntry(row.control.id)} />
            : undefined}
          actionsNote="Reset clears the applicability decision, status and justification for this control."
        />
      </ControlRow>
    );
  }

  return (
    <div>
      <SummaryBar
        label={cap(nounPlural)}
        done={soaApplicable}
        total={soaTotal}
        progressLabel="Applicable"
        cells={[
          { key: "unset", value: soaUnset, label: "Not reviewed", color: theme.color.subtle },
          { key: "yes", value: soaApplicable, label: "Applicable", color: theme.color.successText },
          { key: "no", value: soaNotApplicable, label: "Not applicable", color: theme.color.faint },
        ]}
      />

      {novice && (
        <GuidedIntro title={`Deciding which ${nounPlural} apply`} icon={<CheckCircle2 size={18} />}>
          For each {noun} below, first confirm whether it applies to your organisation. Then mark how well you meet it and upload any supporting documents. Your consultant will review and confirm your assessments.
        </GuidedIntro>
      )}

      <ControlFilterBar
        search={search}
        onSearch={setSearch}
        tagFilter={tagFilter}
        onTagFilter={setTagFilter}
        accent={accent}
        count={{ shown: visible.length, total: allRows.length, noun: nounPlural }}
        onExpandAll={domains.length > 0 ? () => setExpansion("all") : undefined}
        onCollapseAll={domains.length > 0 ? () => setExpansion("none") : undefined}
      >
        {canAssess && (
          <button
            type="button"
            onClick={() => selectAllApplicable(visible)}
            disabled={bulkLoading || allApplicable}
            style={{ ...btnSecondary, padding: "6px 12px", fontSize: text.hint.fontSize, opacity: (bulkLoading || allApplicable) ? 0.5 : 1 }}
          >
            {bulkLoading ? "Applying…" : "Mark all shown as applicable"}
          </button>
        )}
      </ControlFilterBar>
      {isTagFilterActive(tagFilter) && !search && (
        <div style={{ ...text.hint, marginBottom: 10 }}>
          {`${visible.length} ${visible.length !== 1 ? nounPlural : noun} tagged ${describeTagFilter(tagData, tagFilter)}${tagFilter.site ? `. Every other ${noun} applies there through the organisation-wide management system.` : ""}`}
        </div>
      )}

      {visible.length === 0 ? (
        <div style={{ ...emptyFrame, padding: "32px 20px", ...text.hint }}>
          {allRows.length === 0
            ? `No ${nounPlural} found for this standard.`
            : isTagFilterActive(tagFilter) && !search
              ? `No ${noun} carries these tags yet. Open a ${noun} and use its tag row to name the department, office or jurisdiction it applies to.`
              : `No ${nounPlural} match your search.`}
        </div>
      ) : (
        <ControlGroupStack>
          {grouped.map((g) => (
            <ControlGroup
              key={g.domain}
              title={g.title}
              hint={g.hint}
              accent={accent}
              count={g.rows.length}
              noun={noun}
              compliant={g.rows.filter((r) => approvedView(r.entry).status === "COMPLIANT").length}
              gap={g.rows.filter((r) => approvedView(r.entry).status === "GAP").length}
              expanded={searching || isGroupOpen(expansion, g.domain)}
              onToggle={() => setExpansion((prev) => toggleGroup(prev, g.domain, domains))}
            >
              {g.rows.map(renderRow)}
            </ControlGroup>
          ))}
          {ungrouped.length > 0 && <ControlListFrame>{ungrouped.map(renderRow)}</ControlListFrame>}
        </ControlGroupStack>
      )}
    </div>
  );
}

/**
 * Applicable / Not applicable on the row itself. Clicking the current choice changes nothing; an
 * undecided control shows neither selected (Reset, in the control's detail, clears a decision).
 */
function ApplicabilityToggle({ value, novice, label, onChange }: {
  value: boolean | null; novice: boolean; label: string; onChange: (value: boolean) => void;
}) {
  const options: Array<{ v: boolean; text: string }> = [
    { v: true, text: novice ? "Applies" : "Applicable" },
    { v: false, text: novice ? "Excluded" : "Not applicable" },
  ];
  return (
    <div role="group" aria-label={`Applicability of ${label}`} style={{ display: "inline-flex", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, overflow: "hidden" }}>
      {options.map((o, i) => {
        const on = value === o.v;
        return (
          <button
            key={String(o.v)}
            type="button"
            aria-pressed={on}
            onClick={() => { if (!on) onChange(o.v); }}
            style={{
              ...text.meta, fontWeight: 600, padding: "3px 9px", border: "none", cursor: on ? "default" : "pointer", whiteSpace: "nowrap",
              borderLeft: i > 0 ? `1px solid ${theme.color.borderStrong}` : "none",
              background: on ? (o.v ? theme.color.accentBg : theme.color.panelAlt) : "transparent",
              color: on ? (o.v ? theme.color.accentText : theme.color.text) : theme.color.subtle,
            }}
          >
            {o.text}
          </button>
        );
      })}
    </div>
  );
}

/** The applicability decision: read first, Edit to change (controls:write). */
function ApplicabilitySection({ applicable, novice, canEdit, onChange }: {
  applicable: boolean | null;
  novice: boolean;
  canEdit: boolean;
  onChange: (value: boolean) => unknown;
}) {
  const [editing, setEditing] = useState(false);
  const current = applicable === true ? (novice ? "Yes, this applies to us" : "Applicable")
    : applicable === false ? (novice ? "No, we can exclude this" : "Not applicable")
    : "Not reviewed yet";
  return (
    <DetailSection title="Applicability" aside={canEdit && !editing ? <EditButton onClick={() => setEditing(true)} /> : undefined}>
      <div style={text.body}>{current}</div>
      {editing && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
          {([
            { value: true, label: novice ? "Yes, this applies to us" : "Applicable" },
            { value: false, label: novice ? "No, we can exclude this" : "Not applicable" },
          ] as const).map((opt) => {
            const active = applicable === opt.value;
            return (
              <button
                key={String(opt.value)}
                type="button"
                onClick={async () => { await onChange(opt.value); setEditing(false); }}
                style={{
                  padding: "6px 12px", borderRadius: 3, fontSize: text.hint.fontSize, fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${active ? theme.color.accent : theme.color.borderStrong}`,
                  background: active ? theme.color.accentBg : "transparent",
                  color: active ? theme.color.accentText : theme.color.muted,
                }}
              >
                {opt.label}
              </button>
            );
          })}
          <button type="button" onClick={() => setEditing(false)} style={{ ...btnSecondary, padding: "6px 12px", fontSize: text.hint.fontSize }}>Cancel</button>
        </div>
      )}
    </DetailSection>
  );
}

function OriginalText({ label, hideLabel, text: original }: { label: string; hideLabel: string; text: string }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ marginTop: 6 }}>
      <button type="button" onClick={() => setShow((v) => !v)} style={{ ...text.meta, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>
        {show ? hideLabel : label}
      </button>
      {show && (
        <div style={{ ...text.meta, marginTop: 4, padding: "6px 10px", background: theme.color.panelAlt, borderRadius: 3, borderLeft: `2px solid ${theme.color.border}` }}>
          {original}
        </div>
      )}
    </div>
  );
}

function ResetButton({ onConfirm }: { onConfirm: () => unknown }) {
  const [confirming, setConfirming] = useState(false);
  if (!confirming) {
    return <button type="button" onClick={() => setConfirming(true)} style={{ ...btnSecondary, padding: "6px 12px", fontSize: text.hint.fontSize }}>Reset</button>;
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, ...text.hint }}>
      <span style={{ color: theme.color.dangerText }}>Reset this entry?</span>
      <button type="button" onClick={async () => { setConfirming(false); await onConfirm(); }} style={{ ...btnSecondary, padding: "5px 10px", fontSize: text.meta.fontSize, color: theme.color.dangerText, borderColor: theme.color.danger }}>Yes, reset</button>
      <button type="button" onClick={() => setConfirming(false)} style={{ ...btnSecondary, padding: "5px 10px", fontSize: text.meta.fontSize }}>Cancel</button>
    </span>
  );
}
